import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { Spin } from "antd";
import { useNavigate } from "react-router-dom";

import { userActions } from "@fitness-app/app-store";

import { supabase, useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { Logger } from "~/utils/Logger";
import { shouldRefreshPermissions } from "./authSessionPermissions";

interface AuthProviderProps {
  children: React.ReactNode;
}

interface AuthContextType {
  user: { email?: string | null; id: string } | null;
}

const AuthContext = React.createContext<AuthContextType>(null!);

export function useAuth() {
  return React.useContext(AuthContext) || {};
}
const AuthProvider = ({ children }: AuthProviderProps): React.ReactElement => {
  const user = useAppSelector((store) => store.user.data);
  const dispatch = useAppDispatch();
  const [isUserResolved, setIsUserResolved] = useState(false);
  const attemptedPermissionsRefresh = useRef(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const userUid = user?.id;

  const getUser = useCallback(async () => {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    try {
      if (error) {
        Logger.error(error);
        return;
      }

      if (!session) {
        return;
      }

      const {
        data: { user: liveUser },
        error: liveUserError,
      } = await supabase.auth.getUser();

      if (liveUserError || !liveUser) {
        Logger.error(liveUserError ?? new Error("Authenticated user could not be refreshed"));
        return;
      }

      let resolvedSession = session;
      let resolvedUser = liveUser;
      if (
        !attemptedPermissionsRefresh.current &&
        shouldRefreshPermissions(session.user.app_metadata, liveUser.app_metadata)
      ) {
        attemptedPermissionsRefresh.current = true;
        const { data, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError) {
          Logger.error(refreshError);
        } else if (data.session) {
          resolvedSession = data.session;
          resolvedUser = data.session.user;
        }
      }

      dispatch(userActions.setLoggedUserData(resolvedUser, resolvedSession, false, resolvedUser.app_metadata));
    } finally {
      setIsUserResolved(true);
    }
  }, [dispatch]);

  useEffect(() => {
    void getUser();
  }, [getUser]);

  useEffect(() => {
    const subscription = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        // The QueryClient outlives the session, so anything cached under the
        // previous user stays readable — and React Query serves it before the
        // refetch lands, or keeps serving it if the refetch fails. Whoever logs
        // in next must not see it.
        queryClient.clear();
      }
      if (event === "SIGNED_OUT" && userUid) {
        dispatch(userActions.logOutUser());
        Sentry.captureEvent({ message: "User signed out", extra: { event } });
      }
      if (event === "SIGNED_IN" && session?.user?.id && !userUid) {
        dispatch(userActions.setLoggedUserData(session.user, session, false, undefined, true));
      }

      if (event === "USER_UPDATED" && session) {
        dispatch(userActions.setLoggedUserData(session.user, session));
      }

      if (event === "TOKEN_REFRESHED" && session) {
        dispatch(userActions.setLoggedUserData(session.user, session));
      }

      if (event === "PASSWORD_RECOVERY") {
        navigate("/user/set-password");
      }
    });

    return subscription.data.subscription.unsubscribe;
  }, [dispatch, navigate, queryClient, userUid]);

  const value = { user };

  if (!isUserResolved) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
